import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../AuthContext';
import { Box, Button, TextField, Select, MenuItem, Modal, Typography } from '@mui/material';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import axios from 'axios';
import FileUpload from '../Internal-Reports/FileUpload';

function FactoryDataExtractor() {
    const { user } = useContext(AuthContext);
    const [configurations, setConfigurations] = useState([]);
    const [selectedConfigIndex, setSelectedConfigIndex] = useState(null);
    const [newConfig, setNewConfig] = useState({ name: "", rowStart: 1, mappings: {} });
    const [fileHeaders, setFileHeaders] = useState([]);
    const [successMessage, setSuccessMessage] = useState(false); // Success modal state
    const apiUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/ex_fac_data_settings`, { withCredentials: true });
                setConfigurations(response.data.configurations || []);
            } catch (error) {
                console.error('Error fetching settings:', error);
            }
        };

        if (user) {
            fetchSettings();
        }
    }, [user, apiUrl]);

    // Function to handle new config input
    const handleNewConfigChange = (field, value) => {
        setNewConfig((prev) => ({
            ...prev,
            [field]: value === null ? "" : value
        }));
    };

    // **Step 1: Add a New Configuration**
    const addNewConfiguration = () => {
        if (!newConfig.name.trim()) return;

        const newConfigEntry = {
            ...newConfig,
            mappings: {}, 
        };

        setConfigurations([...configurations, newConfigEntry]);
        setSelectedConfigIndex(configurations.length);
        setNewConfig({ name: "", rowStart: 1, mappings: {} });
        setFileHeaders([]);
    };

    // **Step 2: Select a Configuration & Load Existing Mappings**
    const handleSelectConfiguration = (index) => {
        setSelectedConfigIndex(index);
        const selectedConfig = configurations[index];

        if (selectedConfig) {
            setNewConfig({ name: selectedConfig.name, rowStart: selectedConfig.rowStart, mappings: selectedConfig.mappings });

            if (fileHeaders.length === 0 && selectedConfig.fileHeaders) {
                setFileHeaders(selectedConfig.fileHeaders);
            }
        }
    };

    // **Step 3: Handle File Upload**
    const parseFile = (file) => {
        const fileExtension = file.name.split('.').pop();
        if (fileExtension === 'csv') {
            parseCSV(file);
        } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
            parseExcel(file);
        } else {
            alert('Unsupported file format');
        }
    };

    const parseCSV = (file) => {
        Papa.parse(file, {
            complete: (result) => {
                const selectedConfig = configurations[selectedConfigIndex];
                const rowStartIndex = selectedConfig?.rowStart - 1 || 0;
                setFileHeaders(result.data[rowStartIndex] || []);
            },
            header: false,
        });
    };

    const parseExcel = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const excelData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
            const rowStartIndex = configurations[selectedConfigIndex]?.rowStart - 1 || 0;
            setFileHeaders(excelData[rowStartIndex] || []);
        };
        reader.readAsArrayBuffer(file);
    };

    // **Step 4: Handle Mapping Change**
    const handleMappingChange = (dbField, csvHeader) => {
        const updatedMappings = { ...configurations[selectedConfigIndex]?.mappings, [dbField]: csvHeader };
        const updatedConfigs = configurations.map((config, index) =>
            index === selectedConfigIndex ? { ...config, mappings: updatedMappings } : config
        );
        setConfigurations(updatedConfigs);
    };

    // **Step 5: Save Configurations to Backend**
    const saveConfigurationToBackend = async () => {
        if (selectedConfigIndex === null) return;
        try {
            await axios.post(`${apiUrl}/api/save_ex_fac_data_settings`, { configurations }, { withCredentials: true });
            setSuccessMessage(true);
        } catch (error) {
            console.error('Error saving configuration:', error);
        }
    };

    // **Step 6: Handle Success Modal Dismiss**
    const handleCloseSuccessModal = () => {
        setSuccessMessage(false);
        setSelectedConfigIndex(null);
        setNewConfig({ name: "", rowStart: 1, mappings: {} });
        setFileHeaders([]);
    };

    return (
        <Box sx={{ padding: 3, minHeight: '100vh' }}>
            <h2>Factory Data Extractor</h2>

            {/* Add New Configuration */}
            <div className="config-section">
                <h3>Add New Configuration</h3>
                <TextField
                    label="Configuration Name"
                    value={newConfig.name ?? ""}
                    onChange={(e) => handleNewConfigChange("name", e.target.value)}
                    fullWidth
                    sx={{ marginBottom: 2 }}
                />
                <TextField
                    label="Row Start"
                    type="number"
                    value={newConfig.rowStart ?? 1}
                    onChange={(e) => handleNewConfigChange("rowStart", e.target.value)}
                    fullWidth
                    sx={{ marginBottom: 2 }}
                />
                <Button onClick={addNewConfiguration} variant="contained">
                    Add Configuration
                </Button>
            </div>

            {/* Configuration Selector */}
            {configurations.length > 0 && (
                <div>
                    <h3>Select Configuration</h3>
                    <Select
                        value={selectedConfigIndex ?? ""}
                        onChange={(e) => handleSelectConfiguration(e.target.value)}
                        displayEmpty
                    >
                        <MenuItem value="" disabled>Select Configuration</MenuItem>
                        {configurations.map((config, index) => (
                            <MenuItem key={index} value={index}>
                                {config.name}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            )}

            {/* File Upload */}
            {selectedConfigIndex !== null && (
                <>
                    <p>{configurations[selectedConfigIndex].rowStart}</p>
                    <div>
                        <FileUpload onFileUpload={parseFile} />
                    </div>
                </>
            )}

            {/* Mapping Section */}
            {fileHeaders.length > 0 && selectedConfigIndex !== null && (
                <div className="mapping-section">
                    <h3>Map Database Fields to CSV Columns</h3>
                    <table className="mapping-table">
                        <thead>
                            <tr>
                                <th>Database Field</th>
                                <th>CSV Column</th>
                            </tr>
                        </thead>
                        <tbody>
                            {['date', 'brand', 'product', 'quantity', 'customer_name', 'address', 'unit_selling_price', 'value'].map((dbField) => (
                                <tr key={dbField}>
                                    <td>{dbField}</td>
                                    <td>
                                        <Select
                                            value={configurations[selectedConfigIndex]?.mappings[dbField] ?? ""}
                                            onChange={(e) => handleMappingChange(dbField, e.target.value)}
                                            displayEmpty
                                        >
                                            <MenuItem value="">Select Column</MenuItem>
                                            {fileHeaders.map((header, index) => (
                                                <MenuItem key={index} value={header}>
                                                    {header}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Button onClick={saveConfigurationToBackend} variant="contained">
                        Save Configuration
                    </Button>
                </div>
            )}

            <Modal open={successMessage} onClose={handleCloseSuccessModal}>
                <Box sx={{ p: 4, backgroundColor: "white", margin: "auto", textAlign: "center" }}>
                    <Typography variant="h6">Configuration Successfully Saved!</Typography>
                    <Button onClick={handleCloseSuccessModal} variant="contained" sx={{ marginTop: 2 }}>
                        OK
                    </Button>
                </Box>
            </Modal>
        </Box>
    );
}

export default FactoryDataExtractor;
